/**
 * The web app's portals: its shells, one route folder each (`/administrator/...`, `/principal/...`).
 * A portal is a folder of pages, not a role: which roles open in which portal comes from Fabrica
 * (each role's web home and menus, see `webRolesOf`), never from a list of role codes.
 */
export const PORTALS = ['administrator', 'principal', 'faculty', 'student', 'parent'] as const;

/** A web portal: the shell, routes and built-in menus under one route folder. */
export type Portal = (typeof PORTALS)[number];

/** A Fabrica role code as the API returns it (e.g. `librarian`); any string, never a fixed list. */
export type RoleCode = string;

/** Portal names, for sample data only (signed in, the API's role name shows). */
export const PORTAL_LABELS: Record<Portal, string> = {
  administrator: 'Administrator',
  principal: 'Principal',
  faculty: 'Faculty',
  student: 'Student',
  parent: 'Parent',
};

/**
 * Each portal's home page in sample data (signed in, a role's home is its Fabrica web home).
 * Administrator's is the all-menus sitemap at its base route, not the dashboard.
 */
export const PORTAL_HOME_PATH: Record<Portal, string> = {
  administrator: '/administrator',
  principal: '/principal/dashboard',
  faculty: '/faculty/dashboard',
  student: '/student/dashboard',
  parent: '/parent/dashboard',
};

/** localStorage key for the active role (read and written by the global store). */
export const ROLE_STORAGE_KEY = 'camptain.role';

export function isPortal(value: unknown): value is Portal {
  return typeof value === 'string' && (PORTALS as readonly string[]).includes(value);
}

/** The portal a path lies in, from its first segment (`/principal/staff` → `principal`); null for none. */
export function portalOfPath(path: string): Portal | null {
  const segment = path.split(/[?#]/)[0]?.split('/')[1] ?? '';
  return isPortal(segment) ? segment : null;
}

/** The portal of a shell as the API names it (`/administrator`); null when the web app has no such portal. */
export function portalOfShell(shell: string): Portal | null {
  const name = shell.replace(/^\/+|\/+$/g, '');
  return isPortal(name) ? name : null;
}

/** The portals `paths` (a role's web menu paths) lead to, in `PORTALS` order; `[]` when none. */
export function portalsOfPaths(paths: readonly string[]): Portal[] {
  const reached = new Set(paths.map(portalOfPath));
  return PORTALS.filter((portal) => reached.has(portal));
}

/** The configured sample-data portal: `VITE_DEFAULT_ROLE` when it names a portal, then `administrator`. */
export function getDefaultRole(): Portal {
  const fromEnv = import.meta.env.VITE_DEFAULT_ROLE;
  return isPortal(fromEnv) ? fromEnv : 'administrator';
}
